import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { setToken } from '../utils/auth';

const Login = () => {
  const [formData, setFormData] = useState({
    email: 'doctor@example.com',
    password: 'password123'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(formData.email, formData.password);
      setToken(response.access_token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = '/google-oauth.html';
  };

  // Particle animation effect
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let particles = [];
    let width, height;
    let mouse = { x: null, y: null };
    let animationFrameId;

    const colors = ['#4285F4', '#EA4335', '#FBBC05', '#34A853', '#1d4ed8'];

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.vx = (Math.random() - 0.5) * 1;
        this.vy = (Math.random() - 0.5) * 1;
        this.size = Math.random() * 3 + 1;
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.type = Math.random() > 0.7 ? 1 : 0;
        this.dashLength = Math.random() * 10 + 5;
        this.dashAngle = Math.random() * Math.PI * 2;
      }

      draw() {
        ctx.beginPath();
        if (this.type === 0) {
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
          ctx.fillStyle = this.color;
          ctx.fill();
        } else {
          ctx.save();
          ctx.translate(this.x, this.y);
          ctx.rotate(this.dashAngle);
          ctx.rect(-this.dashLength / 2, -this.size / 2, this.dashLength, this.size);
          ctx.fillStyle = this.color;
          ctx.fill();
          ctx.restore();
        }
      }

      update() {
        if (mouse.x != null) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          let forceDirectionX = dx / distance;
          let forceDirectionY = dy / distance;
          let maxDistance = 250;
          let force = (maxDistance - distance) / maxDistance;

          if (distance < maxDistance) {
            const angle = Math.atan2(dy, dx);
            this.vx += forceDirectionX * force * 0.5 - Math.sin(angle) * 0.1;
            this.vy += forceDirectionY * force * 0.5 + Math.cos(angle) * 0.1;
          }
        }

        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.98;
        this.vy *= 0.98;

        if (this.x < 0) this.x = width;
        if (this.x > width) this.x = 0;
        if (this.y < 0) this.y = height;
        if (this.y > height) this.y = 0;

        this.vx += (Math.random() - 0.5) * 0.05;
        this.vy += (Math.random() - 0.5) * 0.05;

        if (this.type === 1) {
          this.dashAngle += 0.02;
        }

        this.draw();
      }
    }

    const init = () => {
      particles = [];
      let numberOfParticles = (width * height) / 10000;
      for (let i = 0; i < numberOfParticles; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      resize();
      init();
    };

    const handleMouseMove = (e) => {
      mouse.x = e.x;
      mouse.y = e.y;
    };

    const handleMouseOut = () => {
      mouse.x = undefined;
      mouse.y = undefined;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseOut);

    resize();
    init();
    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseOut);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <div className="min-h-screen relative flex flex-col overflow-hidden bg-gray-50">
      {/* Particle Canvas */}
      <div className="absolute top-0 left-0 w-full h-full z-0 overflow-hidden pointer-events-none">
        <canvas ref={canvasRef} className="w-full h-full"></canvas>
      </div>

      {/* Navigation */}
      <nav className="relative z-50 w-full px-6 py-6 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="bg-blue-700 text-white p-1.5 rounded-full flex items-center justify-center w-8 h-8 md:w-10 md:h-10">
            <span className="text-lg md:text-xl">🏥</span>
          </div>
          <span className="font-bold text-lg md:text-xl tracking-tight text-slate-900">
            AnesthetistTracker
          </span>
        </div>
        <div className="flex items-center gap-6">
          <div className="hidden md:flex gap-6 text-sm font-medium text-slate-600">
            <a className="hover:text-blue-700 transition-colors" href="#">About</a>
            <a className="hover:text-blue-700 transition-colors" href="#">Help</a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 flex-grow flex flex-col items-center justify-center text-center px-4 mt-[-60px]">
        <div className="max-w-4xl mx-auto space-y-8">
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.1]">
            Anesthetist <br />
            <span className="text-slate-800">Service Tracker</span>
          </h1>

          {!showEmailForm ? (
            <div className="pt-4 space-y-4">
              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleLogin}
                className="group bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 rounded-full font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-xl shadow-blue-700/30"
              >
                <div className="bg-white p-1.5 rounded-full flex items-center justify-center w-8 h-8">
                  <svg height="18" viewBox="0 0 24 24" width="18" xmlns="http://www.w3.org/2000/svg">
                    <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                      <path d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" fill="#4285F4"></path>
                      <path d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" fill="#34A853"></path>
                      <path d="M -21.484 53.529 C -21.734 52.769 -21.864 51.959 -21.864 51.129 C -21.864 50.299 -21.734 49.489 -21.484 48.729 L -21.484 45.639 L -25.464 45.639 C -26.284 47.269 -26.754 49.129 -26.754 51.129 C -26.754 53.129 -26.284 54.989 -25.464 56.619 L -21.484 53.529 Z" fill="#FBBC05"></path>
                      <path d="M -14.754 43.769 C -12.984 43.769 -11.424 44.379 -10.174 45.579 L -6.714 42.119 C -8.804 40.179 -11.514 39.019 -14.754 39.019 C -19.444 39.019 -23.494 41.719 -25.464 45.639 L -21.484 48.729 C -20.534 45.879 -17.884 43.769 -14.754 43.769 Z" fill="#EA4335"></path>
                    </g>
                  </svg>
                </div>
                Sign in with Google
              </button>

              {/* Or Divider */}
              <div className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gray-50 text-gray-500 font-medium">Or</span>
                </div>
              </div>

              {/* Email Sign In Button */}
              <button
                onClick={() => setShowEmailForm(true)}
                className="bg-white hover:bg-gray-50 text-slate-700 px-8 py-4 rounded-full font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg border border-gray-200"
              >
                <span className="text-2xl">📧</span>
                Sign in with Email
              </button>
            </div>
          ) : (
            <div className="pt-4 max-w-md mx-auto">
              <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
                <button
                  onClick={() => setShowEmailForm(false)}
                  className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-2"
                >
                  <span>←</span> Back
                </button>

                <h2 className="text-2xl font-bold text-slate-900 mb-6">Sign in with Email</h2>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent"
                      placeholder="doctor@example.com"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-700 focus:border-transparent"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleChange}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-700 hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-700/30"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>
                </form>

                <div className="mt-6 text-center text-xs text-gray-500 bg-gray-50 p-4 rounded-lg">
                  <p className="font-medium mb-1">Demo Credentials:</p>
                  <p>Email: doctor@example.com</p>
                  <p>Password: password123</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Floating Elements */}
        <div className="absolute bottom-20 left-10 md:left-20 max-w-[300px] text-left hidden lg:block z-20">
          <div className="bg-white shadow-lg p-6 rounded-3xl border border-gray-100 relative overflow-hidden group hover:shadow-xl transition-shadow">
            <div className="absolute -right-4 -top-4 w-20 h-20 bg-green-100 rounded-full flex items-center justify-center opacity-50 group-hover:scale-110 transition-transform">
              <span className="text-green-500 text-3xl mt-4 mr-4">💊</span>
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2 relative z-10">
              Anesthetist<br />Service Tracker
            </h3>
            <p className="text-xs text-slate-500 font-medium relative z-10">
              Professional Utility meets Playful Design.
            </p>
            <div className="mt-4 flex gap-8">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Active Cases</p>
                <p className="text-xl font-bold text-slate-900">1,240+</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Uptime</p>
                <p className="text-xl font-bold text-slate-900">99.9%</p>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-24 right-10 md:right-24 w-24 h-24 bg-blue-700 rounded-full flex items-center justify-center shadow-lg shadow-blue-700/50 z-20 group hover:scale-110 transition-transform cursor-pointer">
          <span className="text-white text-4xl group-hover:animate-pulse">📋</span>
        </div>

        <div className="absolute bottom-10 right-40 w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center z-10 hidden lg:flex">
          <span className="text-blue-700 text-3xl">📊</span>
        </div>
      </main>

      {/* Footer */}
      <footer className="absolute bottom-4 right-6 text-xs text-slate-400 z-50 hidden md:block">
        © 2024 Anesthetist Service Tracker. Built for precision.
      </footer>
    </div>
  );
};

export default Login;
